import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';

import { ComparisonMethod } from './types';

type FetchComparisonMethodsParams = { version_1: string; version_2: string; system_model: string };

const fetchComparisonMethods = async (params: FetchComparisonMethodsParams) => {
  const { data } = await api.get(`/spold/comparison/methods`, { params });

  return data;
};

/**
 * LCIA methods offered by both compared versions. The backend does the intersection, so the FE no
 * longer fetches each release's method list and matches them by name.
 *
 * Indicators are identical across datasets within a release, so the two versions plus the system
 * model identify the method sets without needing either dataset id. Cross-system-model comparison is
 * blocked upstream, so both datasets share the one system model.
 */
const useComparisonMethods = (version1?: string, version2?: string, systemModel?: string) =>
  useQuery<ComparisonMethod[], AxiosError>({
    queryKey: ['comparisonMethods', version1, version2, systemModel],
    queryFn: () =>
      fetchComparisonMethods({
        version_1: version1 as string,
        version_2: version2 as string,
        system_model: systemModel as string,
      }),
    // A version pair's shared methods are fixed once released, so there is nothing to refetch.
    enabled: Boolean(version1 && version2 && systemModel),
    staleTime: Infinity,
  });

export default useComparisonMethods;
